import React, { useEffect, useState } from "react";
import { makeStyles, createStyles, Theme } from "@material-ui/core/styles";
import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  MenuItem,
  Select,
} from "@material-ui/core";
import { useTranslation } from "react-i18next";
import Link from "@material-ui/core/Link/Link";
import { useAppDispatch, useAppSelector } from "../../app/appHooks";
import { StackLogo } from "../StackLogo";
import {
  CommunityTypes,
  ReducerStates,
  Views,
} from "../../../helpers/constants";
import {
  finishAccountCreation,
  getCommunitiesAccountCreation,
} from "../loginSlice";
import { useHistory } from "react-router-dom";
import { getCommunityDisplayName } from "../../header/CommunityDropdown";

// Component styles
const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    loginFormContainer: {
      width: "380px",
      marginTop: theme.spacing(3),
      display: "flex",
      alignItems: "center",
      flexDirection: "column",
      [theme.breakpoints.down("sm")]: {
        width: "360px",
      },
      [theme.breakpoints.down("xs")]: {
        width: "100%",
      },
    },
    textField: {
      marginBottom: theme.spacing(2),
    },
    helperText: {
      minHeight: "20px",
    },
    subtitleText: {
      textAlign: "center",
      fontFamily: theme.typography.secondaryFontFamily,
      fontWeight: 500,
      fontSize: theme.typography.subtitle1.fontSize,
      padding: theme.spacing(0, 2),
      marginBottom: theme.spacing(3),
    },
    selectMenu: {
      padding: theme.spacing(2),
    },
    menuItemText: {
      padding: theme.spacing(1),
    },
    notInstalledContainer: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      textAlign: "center",
      padding: theme.spacing(2),
      maxWidth: "380px",
    },
  })
);

const APP_STORE_URL = "https://apps.apple.com/us/app/stackcare/id1471170266";
const PLAY_STORE_URL =
  "https://play.google.com/store/apps/details?id=com.stacklighting.stackcare";

export const SelectCommunityForm = (props: IProps) => {
  /* ---- Hooks ---- */
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const classes = useStyles();
  const history = useHistory();

  /* ---- State ---- */
  const [selectedCommunity, setSelectedCommunity] = useState("");
  const [formError, setFormError] = useState("");

  /* ---- Selectors ---- */
  const loginStatus = useAppSelector((state) => state.loginState.state);
  const communities = useAppSelector(
    (state) => state.loginState.userCommunities
  );

  /* ---- Effects ---- */
  // Get communities for the user
  useEffect(() => {
    if (loginStatus === ReducerStates.IDLE) {
      dispatch(getCommunitiesAccountCreation());
    }
  }, [loginStatus, dispatch]);

  const installedCommunities = communities?.filter(
    (c) => c.community_type === CommunityTypes.pro || c.is_installed !== false
  );

  const isOnlyUninstalledAip =
    communities !== undefined &&
    communities.length > 0 &&
    installedCommunities?.length === 0;

  // If the user has 1 installed community, auto-select it
  useEffect(() => {
    if (
      loginStatus === ReducerStates.SUCCEEDED &&
      communities !== undefined &&
      !isOnlyUninstalledAip
    ) {
      if (
        installedCommunities !== undefined &&
        installedCommunities.length <= 1
      ) {
        if (installedCommunities.length === 1) {
          dispatch(finishAccountCreation(installedCommunities[0].id));
        } else if (installedCommunities.length === 0) {
          dispatch(finishAccountCreation());
        }
        history.push(Views.DASHBOARD);
      }
    }
  }, [
    loginStatus,
    dispatch,
    communities,
    installedCommunities,
    history,
    isOnlyUninstalledAip,
  ]);

  /* ---- Methods ---- */
  const handleSelectedCommunityChange = (
    e: React.ChangeEvent<{
      name?: string | undefined;
      value: unknown;
    }>
  ): void => {
    const { value } = e.target;
    setSelectedCommunity(value as string);
    setFormError("");
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    if (selectedCommunity) {
      dispatch(finishAccountCreation(selectedCommunity));
      history.push(Views.DASHBOARD);
    }
  };

  const disableSubmit = selectedCommunity === "" || formError !== "";

  if (isOnlyUninstalledAip) {
    return (
      <React.Fragment>
        <StackLogo />
        <Box className={classes.notInstalledContainer}>
          <h3>
            {t("aip_not_installed_message_prefix")}
            <Link
              href={APP_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              iOS
            </Link>
            {t("aip_not_installed_message_or")}
            <Link
              href={PLAY_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              Android
            </Link>
            {t("aip_not_installed_message_suffix")}
          </h3>
        </Box>
      </React.Fragment>
    );
  }

  return (
    <React.Fragment>
      <StackLogo />
      <form
        id="validate-email-form"
        noValidate
        className={classes.loginFormContainer}
        autoComplete="off"
        onSubmit={handleSubmit}
      >
        <p className={classes.subtitleText}>
          {t("communiy_selection_helper_text")}
        </p>

        <FormControl variant="filled" fullWidth>
          <Select
            displayEmpty={true}
            id="community-select"
            value={selectedCommunity}
            onChange={handleSelectedCommunityChange}
            error={formError !== ""}
            classes={{ selectMenu: classes.selectMenu }}
            renderValue={(value) => {
              if (value === "") return t("select_community");
              const community = installedCommunities?.find(
                (c) => c.id === value
              );
              return community ? getCommunityDisplayName(community) : "";
            }}
          >
            {installedCommunities?.map((community) => {
              return (
                <MenuItem value={community.id}>
                  <span className={classes.menuItemText}>
                    {getCommunityDisplayName(community)}
                  </span>
                </MenuItem>
              );
            })}
          </Select>

          <FormHelperText
            id="verification_code-error"
            className={classes.helperText}
            component="span"
            error={formError !== ""}
          >
            {formError}
          </FormHelperText>
        </FormControl>
        <Button
          type="submit"
          variant={disableSubmit ? "outlined" : "contained"}
          color="primary"
          size="large"
          disabled={disableSubmit}
          className={classes.textField}
        >
          {t("next")}
        </Button>
      </form>
    </React.Fragment>
  );
};
interface IProps {}
